import React, { useState, useEffect } from 'react';
import AppSelect from '@nxsys/components/AppSelect';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { TaxWeekSelectProps } from './type';
import { getOptionsAllTaxWeekTaxMonth } from '@nxsys/utils/taxYear';

const TaxWeekMonthSelect: React.FC<TaxWeekSelectProps> = ({
  value,
  onChange,
  initValue,
  onFilter,
  className,
  placeholder,
  disabled = false,
  allowClear = true,
  paymentFrequency = 'WEEKLY',
}) => {
  const { messages } = useTypedIntl();
  const taxtWeekOptions = getOptionsAllTaxWeekTaxMonth(paymentFrequency);

  const isControlled = value !== undefined;

  const [internalValue, setInternalValue] = useState<number | undefined>(() => {
    if (isControlled) return undefined;
    return initValue;
  });

  const currentValue = isControlled ? value : internalValue;

  const selectedOption =
    currentValue !== undefined
      ? taxtWeekOptions.find((opt) => opt.value === currentValue)
      : undefined;

  useEffect(() => {
    if (!isControlled && initValue !== undefined) {
      setInternalValue(initValue);
    }
  }, [initValue, isControlled]);

  const handleChange = (
    option: { label: string; value: string } | undefined,
  ) => {
    const selectedValue = option?.value
      ? parseInt(option.value, 10)
      : undefined;

    if (!isControlled) {
      setInternalValue(selectedValue);
    }

    if (onChange) {
      onChange(selectedValue);
    }

    if (onFilter) {
      onFilter({ taxPeriod: selectedValue });
    }
  };

  const handleClear = () => {
    if (!isControlled) {
      setInternalValue(undefined);
    }

    if (onChange) {
      onChange(undefined);
    }

    if (onFilter) {
      onFilter({ taxPeriod: undefined });
    }
  };

  return (
    <div className={className}>
      <AppSelect
        placeholder={placeholder || messages['common.taxPeriod'] || 'Tax Period'}
        allowClear={allowClear}
        value={selectedOption || undefined}
        onChange={handleChange}
        onClear={handleClear}
        disabled={disabled}
        options={taxtWeekOptions.map((option) => ({
          label: option.label,
          value: option.value.toString(),
        }))}
        className="min-w-[210px] lg:!min-w-[210px] sm:!w-full md:!w-full"
      />
    </div>
  );
};

export default TaxWeekMonthSelect;
